import { useState, useMemo } from 'react';
import { useRouter, useCurrentStateAndParams } from '@uirouter/react';

import { notifyError, notifySuccess } from '@/portainer/services/notifications';
import { useEnvironmentList } from '@/react/portainer/environments/queries';
import type { Environment } from '@/react/portainer/environments/types';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';
import type { EnvironmentGroup } from '@/react/portainer/environments/environment-groups/types';

import { PageHeader } from '@@/PageHeader';
import { Stepper } from '@@/Stepper/Stepper';

import {
  getEnvironmentTypeDisplayName,
  getPolicyTypeDisplayName,
  PolicyType,
  POLICY_TYPES,
  EnvironmentTypeFilter,
  CategoryFilter,
  PolicyTemplate,
} from '../types';
import { useCreatePolicyMutation } from '../queries/useCreatePolicyMutation';
import { usePolicyTemplates } from '../queries/usePolicyTemplates';
import {
  getEnvironmentTypesFromPolicyType,
  getCompatibleGroupsCount,
} from '../utils/policyTypeUtils';
import { getAllPolicyTypesMetadata } from '../utils/policy-types-metadata';
import { filterPolicyTypes } from '../utils/filterPolicyTypes';
import { buildKubernetesRBACPayload } from '../components/kubernetes/rbac/buildKubernetesRBACPayload';
import { buildKubernetesSecurityPayload } from '../components/kubernetes/security/buildKubernetesSecurityPayload';
import { buildKubernetesNetworkSecurityPayload } from '../components/kubernetes/network-security/buildKubernetesNetworkSecurityPayload';
import { buildVulnerabilityScanningPayload } from '../components/kubernetes/vulnerability-scanning/buildVulnerabilityScanningPayload';
import {
  isNamespaceScopeMode,
  isRescanIntervalUnit,
  VulnerabilityScanningFormValues,
} from '../components/kubernetes/vulnerability-scanning/types';
import { buildKubernetesSetupPayload } from '../components/kubernetes/setup/buildKubernetesSetupPayload';
import { buildKubernetesRegistryPayload } from '../components/kubernetes/registry/buildKubernetesRegistryPayload';
import { buildDockerRBACPayload } from '../components/docker/rbac/buildDockerRBACPayload';
import { buildDockerSecurityPayload } from '../components/docker/security/buildDockerSecurityPayload';
import { buildDockerSetupPayload } from '../components/docker/setup/buildDockerSetupPayload';
import { buildDockerRegistryPayload } from '../components/docker/registry/buildDockerRegistryPayload';
import { buildDockerCleanupPayload } from '../components/docker/cleanup/buildDockerCleanupPayload';
import { KubernetesRbacFormValues } from '../components/kubernetes/rbac/types';
import { KubernetesNetworkSecurityTypeSpecificData } from '../components/kubernetes/network-security/types';
import { KubernetesSecurityTypeSpecificData } from '../components/kubernetes/security/types';
import { KubernetesSetupFormData } from '../components/kubernetes/setup/types';
import { KubernetesRegistryFormValues } from '../components/kubernetes/registry/types';
import { DockerRBACFormValues } from '../components/docker/rbac/types';
import { DockerSecurityFormData } from '../components/docker/security/types';
import { DockerSetupFormData } from '../components/docker/setup/types';
import { DockerRegistryFormValues } from '../components/docker/registry/types';
import { DockerCleanupFormValues } from '../components/docker/cleanup/types';
import { CreatePolicyData } from '../queries/types';
import { PolicyFormByType } from '../components/PolicyFormByType';
import { hasSelectedGroupsWithConflicts } from '../components/PolicyConfigurationFormSection';
import {
  buildChangeConfirmationPolicyPayload,
  isChangeConfirmationFormData,
} from '../components/change-confirmation/types';
import {
  buildObservabilityPayload,
  ObservabilityK8sFormValues,
} from '../components/kubernetes/observability/types';
import { buildPodSecurityStandardsPayload } from '../components/kubernetes/pod-security-standards/buildPodSecurityStandardsPayload';
import { PodSecurityStandardsFormValues } from '../components/kubernetes/pod-security-standards/types';
import { buildAutomaticNamespaceProvisioningPayload } from '../components/kubernetes/automatic-namespace-provisioning/buildAutomaticNamespaceProvisioningPayload';
import { AutomaticNamespaceProvisioningFormValues } from '../components/kubernetes/automatic-namespace-provisioning/types';

import { PolicyTypeSelectionStep } from './PolicySelectionStep';

function buildWizardSteps(canConfigure: boolean) {
  return [
    { id: 'select-type', label: 'Select policy' },
    { id: 'configure', label: 'Review and edit', enabled: canConfigure },
  ];
}

type Step = 'select-type' | 'configure';

const CUSTOM_TEMPLATE_ID = 'custom';

export function PolicyCreateView() {
  const router = useRouter();
  const { params } = useCurrentStateAndParams();
  const urlPolicyType = isValidPolicyType(params.type)
    ? params.type
    : undefined;
  const urlStep = typeof params.step === 'string' ? params.step : undefined;
  const urlTemplateId =
    typeof params.template === 'string' ? params.template : undefined;

  // Derive step from URL param — URL is single source of truth
  const step = useMemo((): Step => {
    if (!urlStep && urlPolicyType) return 'configure';
    if (urlStep === 'configure') return 'configure';
    return 'select-type';
  }, [urlStep, urlPolicyType]);

  const [selectedType, setSelectedType] = useState<PolicyType | null>(
    urlPolicyType ?? 'change-confirmation'
  );

  // Filter states hoisted from PolicyTypeSelector
  const [environmentTypeFilter, setEnvironmentTypeFilter] =
    useState<EnvironmentTypeFilter>('all');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Data for type selector
  const { environments, totalCount } = useEnvironmentList();
  const groupsQuery = useGroups();
  const groups = useMemo(() => groupsQuery.data || [], [groupsQuery.data]);

  const createPolicyMutation = useCreatePolicyMutation();

  // Derive the displayed policy type from filters
  const derivedPolicyType = useDerivedPolicyType(
    environments,
    groups,
    environmentTypeFilter,
    categoryFilter,
    searchQuery,
    selectedType
  );

  const policyTemplatesQuery = usePolicyTemplates();

  // undefined = no explicit choice (use resolvedDefault)
  // null      = user explicitly chose "Custom" (no template)
  // template  = user's explicit template pick
  const [templateOverride, setTemplateOverride] = useState<
    PolicyTemplate | null | undefined
  >(undefined);

  const resolvedDefault = useMemo(
    () =>
      resolveDefaultTemplate(
        policyTemplatesQuery.data,
        urlTemplateId,
        derivedPolicyType
      ),
    [policyTemplatesQuery.data, urlTemplateId, derivedPolicyType]
  );

  const effectiveTemplate =
    templateOverride === undefined ? resolvedDefault : templateOverride;

  const waitingForTemplate =
    step === 'configure' &&
    !!urlTemplateId &&
    urlTemplateId !== CUSTOM_TEMPLATE_ID &&
    !policyTemplatesQuery.data;

  const wizardSteps = buildWizardSteps(!!derivedPolicyType);

  // Wizard navigation
  const currentStepIndex = step === 'select-type' ? 0 : 1;
  const canGoBack = step === 'configure';

  const title = getTitle();

  return (
    <div className="form-horizontal pb-20">
      <PageHeader
        title={title}
        breadcrumbs={[
          { label: 'Fleet Governance Policies', link: 'portainer.policies' },
          title,
        ]}
        reload
      />

      {/* Stepper - always visible */}
      <div className="row">
        <div className="col-sm-12">
          <Stepper
            steps={wizardSteps}
            currentStepIndex={currentStepIndex}
            onStepClick={(index) => {
              if (index === 0) navigateToStep('select-type', undefined, true);
              if (index === 1 && derivedPolicyType) {
                setSelectedType(derivedPolicyType);
                navigateToStep('configure', derivedPolicyType);
              }
            }}
          />
        </div>
      </div>

      {/* Step 1: Type Selection */}
      {step === 'select-type' && (
        <PolicyTypeSelectionStep
          value={derivedPolicyType}
          onChange={(type) => {
            setSelectedType(type);
            setTemplateOverride(undefined);
          }}
          onTemplateSelect={setTemplateOverride}
          selectedTemplateId={effectiveTemplate?.id}
          environments={environments}
          groups={groups}
          totalEnvironmentCount={totalCount}
          environmentFilter={environmentTypeFilter}
          onEnvironmentFilterChange={setEnvironmentTypeFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          onNext={goToNextStep}
        />
      )}

      {/* Step 2: Configure */}
      {step === 'configure' && derivedPolicyType && !waitingForTemplate && (
        <div className="row">
          <div className="col-sm-12">
            <PolicyFormByType
              type={derivedPolicyType}
              mode="create"
              onSubmit={handleSubmit}
              templateData={effectiveTemplate?.data}
              onBack={goToPreviousStep}
              canGoBack={canGoBack}
            />
          </div>
        </div>
      )}
    </div>
  );

  function navigateToStep(
    stepId: Step,
    policyType?: PolicyType,
    replace = false
  ) {
    router.stateService.go(
      '.',
      {
        step: stepId,
        type: policyType ?? selectedType ?? undefined,
        template:
          stepId === 'configure'
            ? (effectiveTemplate?.id ?? CUSTOM_TEMPLATE_ID)
            : null,
      },
      { location: replace ? 'replace' : true }
    );
  }

  function goToNextStep() {
    if (step === 'select-type' && derivedPolicyType) {
      setSelectedType(derivedPolicyType);
      navigateToStep('configure', derivedPolicyType);
    }
  }

  function goToPreviousStep() {
    if (step === 'configure') {
      navigateToStep('select-type', undefined, true);
    }
  }

  function getTitle() {
    if (step === 'configure' && derivedPolicyType) {
      const envTypesArr = getEnvironmentTypesFromPolicyType(derivedPolicyType);
      const allEnvTypesCount = 4; // kubernetes, docker, podman, swarm
      if (envTypesArr.length === allEnvTypesCount) {
        return `Create ${getPolicyTypeDisplayName(derivedPolicyType)} policy`;
      }
      const envPrefix = getEnvironmentTypeDisplayName(envTypesArr[0]);
      return `Create ${envPrefix} ${getPolicyTypeDisplayName(
        derivedPolicyType
      )} policy`;
    }
    return 'Create policy';
  }

  async function handleSubmit(values: unknown) {
    if (!derivedPolicyType) return;

    const formValues = values as {
      name: string;
      environmentGroupIds: Array<number>;
    };

    let payloadData: CreatePolicyData;
    try {
      payloadData = buildPolicyPayload(derivedPolicyType, values);
    } catch (error) {
      notifyError('Error', error as Error);
      return;
    }

    // Check if any selected groups have conflicts that need to be overridden
    const needsOverride = hasSelectedGroupsWithConflicts(
      formValues.environmentGroupIds,
      groups,
      derivedPolicyType
    );

    await createPolicyMutation.mutateAsync(
      {
        Name: formValues.name,
        Type: derivedPolicyType,
        EnvironmentGroups: formValues.environmentGroupIds,
        Data: payloadData,
        AllowOverride: needsOverride,
      },
      {
        onSuccess: () => {
          notifySuccess('Success', 'Policy created successfully');
          router.stateService.go('portainer.policies');
        },
      }
    );
  }
}

function resolveDefaultTemplate(
  templates: PolicyTemplate[] | undefined,
  urlTemplateId: string | undefined,
  policyType: PolicyType | null
): PolicyTemplate | null {
  if (
    !templates ||
    !policyType ||
    !urlTemplateId ||
    urlTemplateId === CUSTOM_TEMPLATE_ID
  ) {
    return null;
  }
  return templates.find((t) => t.id === urlTemplateId) ?? null;
}

// Type guards for each form data type
function isKubernetesRbacFormData(
  value: unknown
): value is KubernetesRbacFormValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessList' in value &&
    Array.isArray((value as KubernetesRbacFormValues).accessList)
  );
}

function isKubernetesSecurityFormData(
  value: unknown
): value is KubernetesSecurityTypeSpecificData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'restrictDefaultNamespace' in value &&
    'enablePodSecurityConstraints' in value
  );
}

function isKubernetesNetworkSecurityFormData(
  value: unknown
): value is KubernetesNetworkSecurityTypeSpecificData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'ingressRules' in value &&
    'egressRules' in value
  );
}

function isKubernetesSetupFormData(
  value: unknown
): value is KubernetesSetupFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'useLoadBalancer' in value &&
    'ingressControllers' in value &&
    'storageClasses' in value
  );
}

function isKubernetesRegistryFormData(
  value: unknown
): value is KubernetesRegistryFormValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessRows' in value &&
    Array.isArray((value as KubernetesRegistryFormValues).accessRows) &&
    ((value as KubernetesRegistryFormValues).accessRows.length === 0 ||
      'namespaces' in (value as KubernetesRegistryFormValues).accessRows[0])
  );
}

function isDockerRBACFormData(
  value: unknown
): value is Pick<DockerRBACFormValues, 'accessList'> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessList' in value &&
    Array.isArray(
      (value as Pick<DockerRBACFormValues, 'accessList'>).accessList
    )
  );
}

function isDockerSecurityFormData(
  value: unknown
): value is DockerSecurityFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'allowBindMountsForRegularUsers' in value &&
    'allowPrivilegedModeForRegularUsers' in value
  );
}

function isDockerSetupFormData(value: unknown): value is DockerSetupFormData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'enableHostManagementFeatures' in value &&
    'gpus' in value
  );
}

function isDockerRegistryFormData(
  value: unknown
): value is Pick<DockerRegistryFormValues, 'accessRows'> {
  return (
    typeof value === 'object' &&
    value !== null &&
    'accessRows' in value &&
    Array.isArray(value.accessRows) &&
    (value.accessRows.length === 0 ||
      ('userIds' in value.accessRows[0] && 'teamIds' in value.accessRows[0]))
  );
}

function isVulnerabilityScanningFormData(
  value: unknown
): value is VulnerabilityScanningFormValues {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  const formData = value as Partial<VulnerabilityScanningFormValues>;

  return (
    Number.isFinite(formData.rescanIntervalValue) &&
    isRescanIntervalUnit(formData.rescanIntervalUnit) &&
    Number.isFinite(formData.maxConcurrentScans) &&
    typeof formData.targetNamespace === 'string' &&
    isNamespaceScopeMode(formData.namespaceScopeMode)
  );
}

function isPodSecurityStandardsFormData(
  value: unknown
): value is PodSecurityStandardsFormValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'namespaces' in value &&
    Array.isArray((value as PodSecurityStandardsFormValues).namespaces)
  );
}

function isDockerCleanupFormData(
  value: unknown
): value is DockerCleanupFormValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'cleanOldImages' in value &&
    'cleanImagesAtStorageLimit' in value &&
    'cleanupInterval' in value
  );
}

function isAutomaticNamespaceProvisioningFormData(
  value: unknown
): value is AutomaticNamespaceProvisioningFormValues {
  return (
    typeof value === 'object' &&
    value !== null &&
    'userScopes' in value &&
    Array.isArray(value.userScopes) &&
    'namespacePattern' in value &&
    typeof value.namespacePattern === 'string'
  );
}

/**
 * Build the policy payload based on policy type.
 * Uses type guards to validate form values at runtime.
 */
export function buildPolicyPayload(
  type: PolicyType,
  formValues: unknown
): CreatePolicyData {
  switch (type) {
    case 'rbac-k8s':
      if (!isKubernetesRbacFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes RBAC policy');
      }
      return buildKubernetesRBACPayload(formValues);
    case 'security-k8s':
      if (!isKubernetesSecurityFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes Security policy');
      }
      return buildKubernetesSecurityPayload(formValues);
    case 'network-security-k8s':
      if (!isKubernetesNetworkSecurityFormData(formValues)) {
        throw new Error(
          'Invalid form data for Kubernetes Network Security policy'
        );
      }
      return buildKubernetesNetworkSecurityPayload(formValues);
    case 'setup-k8s':
      if (!isKubernetesSetupFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes Setup policy');
      }
      return buildKubernetesSetupPayload(formValues);
    case 'registry-k8s':
      if (!isKubernetesRegistryFormData(formValues)) {
        throw new Error('Invalid form data for Kubernetes Registry policy');
      }
      return buildKubernetesRegistryPayload(formValues);
    case 'rbac-docker':
      if (!isDockerRBACFormData(formValues)) {
        throw new Error('Invalid form data for Docker RBAC policy');
      }
      return buildDockerRBACPayload(formValues);
    case 'security-docker':
      if (!isDockerSecurityFormData(formValues)) {
        throw new Error('Invalid form data for Docker Security policy');
      }
      return buildDockerSecurityPayload(formValues);
    case 'setup-docker':
      if (!isDockerSetupFormData(formValues)) {
        throw new Error('Invalid form data for Docker Setup policy');
      }
      return buildDockerSetupPayload(formValues);
    case 'registry-docker':
      if (!isDockerRegistryFormData(formValues)) {
        throw new Error('Invalid form data for Docker Registry policy');
      }
      return buildDockerRegistryPayload(formValues);
    case 'change-confirmation':
      if (!isChangeConfirmationFormData(formValues)) {
        throw new Error(
          'Invalid form data for Banner and change confirmation policy'
        );
      }
      return buildChangeConfirmationPolicyPayload(formValues);
    case 'cleanup-docker':
      if (!isDockerCleanupFormData(formValues)) {
        throw new Error('Invalid form data for Docker Cleanup policy');
      }
      return buildDockerCleanupPayload(formValues);
    case 'observability-k8s':
      if (
        typeof formValues !== 'object' ||
        formValues === null ||
        !('oneUptimeURL' in formValues)
      ) {
        throw new Error('Invalid form data for Observability policy');
      }
      return buildObservabilityPayload(
        formValues as ObservabilityK8sFormValues
      );
    case 'vulnerability-scanning-k8s':
      if (!isVulnerabilityScanningFormData(formValues)) {
        throw new Error('Invalid form data for Vulnerability Scanning policy');
      }
      return buildVulnerabilityScanningPayload(formValues);
    case 'pod-security-standards-k8s':
      if (!isPodSecurityStandardsFormData(formValues)) {
        throw new Error('Invalid form data for Pod Security Standards policy');
      }
      return buildPodSecurityStandardsPayload(formValues);
    case 'automatic-namespace-provisioning-k8s':
      if (!isAutomaticNamespaceProvisioningFormData(formValues)) {
        throw new Error(
          'Invalid form data for Automatic Namespace Provisioning policy'
        );
      }
      return buildAutomaticNamespaceProvisioningPayload(formValues);
    default:
      throw new Error(`Unknown policy type: ${type}`);
  }
}

/**
 * Get selectable policy types (those with compatible groups)
 */
function getSelectablePolicyTypes(
  filteredPolicyTypes: Array<{ id: PolicyType }>,
  environments: Environment[],
  groups: EnvironmentGroup[]
): PolicyType[] {
  return filteredPolicyTypes
    .filter(
      (metadata) =>
        getCompatibleGroupsCount(metadata.id, environments, groups) > 0
    )
    .map((metadata) => metadata.id);
}

/**
 * Select the default policy type based on available options
 * Priority: 'change-confirmation' → first selectable option → null
 */
function selectDefaultPolicyType(
  selectableOptions: PolicyType[]
): PolicyType | null {
  // Prefer change-confirmation as the default
  if (selectableOptions.includes('change-confirmation')) {
    return 'change-confirmation';
  }

  // Fall back to first selectable option
  if (selectableOptions.length > 0) {
    return selectableOptions[0];
  }

  return null;
}

/**
 * Derives the appropriate policy type from server state and filters.
 *
 * Instead of syncing state with useEffect, we derive the valid selection from available options.
 * This avoids race conditions, extra renders, and preserves the original selection for restoration.
 *
 * Priority: current selection (if valid) → 'rbac-k8s' → first available option
 */
function useDerivedPolicyType(
  environments: Environment[],
  groups: EnvironmentGroup[],
  environmentFilter: EnvironmentTypeFilter,
  categoryFilter: CategoryFilter,
  searchQuery: string,
  currentPolicyType: PolicyType | null
): PolicyType | null {
  return useMemo(() => {
    // Always use all policy types as the base - filterPolicyTypes handles
    // environment type filtering. This ensures policy types are visible even
    // if there are no compatible environment groups.
    const basePolicyTypes = getAllPolicyTypesMetadata();

    // Apply filters using shared utility
    const filteredPolicyTypes = filterPolicyTypes(
      basePolicyTypes,
      environmentFilter,
      categoryFilter,
      searchQuery
    );

    // Get selectable options (those with compatible groups)
    const selectableOptions = getSelectablePolicyTypes(
      filteredPolicyTypes,
      environments,
      groups
    );

    const availableOptions = filteredPolicyTypes.map((metadata) => metadata.id);

    // Preserve the user's current selection if it is still visible, even if unsupported
    if (currentPolicyType && availableOptions.includes(currentPolicyType)) {
      return currentPolicyType;
    }

    // Select default from selectable options
    const defaultSelection = selectDefaultPolicyType(selectableOptions);
    if (defaultSelection) {
      return defaultSelection;
    }

    // As a last resort, return the first visible option (may be unsupported)
    return availableOptions[0] || null;
  }, [
    environments,
    groups,
    environmentFilter,
    categoryFilter,
    searchQuery,
    currentPolicyType,
  ]);
}

function isValidPolicyType(value: unknown): value is PolicyType {
  return POLICY_TYPES.includes(value as PolicyType);
}
